import { create } from 'zustand';
import { getRecurrenceToShare, getShiftToShare } from '@libs/api/shifts';
import { ShiftList } from '@libs/models/shifts/shift';
import dayjs, { Dayjs } from 'dayjs';
import { TIMEZONE_FALLBACK } from '@libs/config';
import { RecurrenceShortListItem } from '@libs/models/shifts';

const FallbackTimezone = TIMEZONE_FALLBACK;

type DateFilterValue = { from: Dayjs; to: Dayjs; timezone: string };

type State = {
  loading: boolean;
  drawerOpen: boolean;
  rotationId: string;
  initiated: boolean;
  shiftsPassedFromOutside: boolean;
  shiftsToShare: ShiftList[];
  totalShifts: number;
  selectedShifts: string[];

  seriesNRotationsLoading: boolean;
  seriesNRotationsToShare: RecurrenceShortListItem[];
  totalSeriesNRotations: number;
  selectedSeriesNRotations: string[];

  currentStep: 'shifts' | 'agencies';
  setCurrentStep: (step: 'shifts' | 'agencies') => void;
  loadSharableShifts: (options: {
    dateFilter: DateFilterValue | null;
    statusFilter: string[];
    locationId: string;
    qualifications?: string[];
  }) => void;
  loadSharableSeriesNRotations: (options: {
    dateFilter: DateFilterValue | null;
    statusFilter: string[];
    locationId: string;
    qualifications?: string[];
  }) => void;
  openShiftShareDrawer: (config?: OpenShiftShareDrawerConfig) => void;
  setSelectedSeriesNRotations: (ids: string[]) => void;
  setSelectedShifts: (ids: string[]) => void;
  closeShiftShareDrawer: () => void;
};

export const useShareDrawerStore = create<State>()((set, get) => ({
  loading: false,
  drawerOpen: false,
  initiated: false,
  shiftsPassedFromOutside: false,
  rotationId: '',
  currentStep: 'shifts',
  shiftsToShare: [],
  selectedShifts: [],
  totalShifts: 0,

  seriesNRotationsLoading: false,
  seriesNRotationsToShare: [],
  totalSeriesNRotations: 0,
  selectedSeriesNRotations: [],

  loadSharableShifts: async (options) => {
    const { dateFilter, statusFilter, locationId, qualifications = [] } = options;
    set({ loading: true });

    try {
      const timezone = dateFilter?.timezone || FallbackTimezone;

      const shifts = await getShiftToShare({
        timezone,
        startDate: dateFilter?.from,
        endDate: dateFilter?.to,
        filters: {
          statuses: { key: 'status', value: statusFilter },
          locations: { key: 'locations', value: [locationId] },
          qualifications: { key: 'qualifications', value: qualifications },
          startDate: {
            key: 'startDate',
            value: { from: dateFilter?.from || dayjs(new Date()), to: dateFilter?.to, timezone },
          },
        },
      });
      set({
        loading: false,
        shiftsToShare: shifts,
        totalShifts: shifts.length,
      });
    } finally {
      set({ loading: false, initiated: true });
    }
  },

  loadSharableSeriesNRotations: async (options) => {
    const { dateFilter, statusFilter, locationId, qualifications = [] } = options;
    set({ seriesNRotationsLoading: true });

    try {
      const timezone = dateFilter?.timezone || FallbackTimezone;
      const recurrences = await getRecurrenceToShare({
        filters: {
          statuses: {
            key: 'statuses',
            value: statusFilter.length ? statusFilter : ['Unassigned', 'Pending', 'Unfilled'],
          },
          locations: { key: 'locations', value: [locationId] },
          qualifications: { key: 'qualifications', value: qualifications },
          startDate: {
            key: 'startDate',
            value: { from: dateFilter?.from || dayjs(new Date()), to: dateFilter?.to, timezone },
          },
        },
      });

      set({
        seriesNRotationsLoading: false,
        seriesNRotationsToShare: recurrences,
        totalSeriesNRotations: recurrences.length,
      });
    } finally {
      set({ seriesNRotationsLoading: false, initiated: true });
    }
  },

  openShiftShareDrawer: (config) => {
    const { rotationId, preselectedShifts, preselectedRecurrences } = config ?? {};
    set({
      rotationId,
      drawerOpen: true,
      selectedShifts: preselectedShifts ?? [],
      selectedSeriesNRotations: preselectedRecurrences ?? [],
      currentStep: preselectedShifts || preselectedRecurrences ? 'agencies' : 'shifts',
      shiftsPassedFromOutside: !!preselectedShifts,
    });
  },
  closeShiftShareDrawer: () => {
    set({
      drawerOpen: false,
      loading: false,
      initiated: false,
      shiftsToShare: [],
      selectedShifts: [],
      seriesNRotationsToShare: [],
      selectedSeriesNRotations: [],
    });
  },
  setSelectedShifts: (ids) => {
    set({ selectedShifts: ids });
  },

  setSelectedSeriesNRotations: (ids) => {
    set({ selectedSeriesNRotations: ids });
  },
  setCurrentStep: (step) => {
    set({ currentStep: step });
  },
}));

type OpenShiftShareDrawerConfig = {
  rotationId?: string;
  preselectedShifts?: string[];
  preselectedRecurrences?: string[];
};
